import {
  useIsMobile,
  WSElement,
  WSScreen
} from "@wingspanhq/fe-component-library";
import * as React from "react";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { BulkReviewPanel } from "../../modules/BulkReview/components/BulkReviewPanel";
import { BulkReviewSidebar } from "../../modules/BulkReview/components/BulkReviewSidebar";
import { AppFooter } from "../../shared/components/AppFooter";
import { useWSStore } from "../../store";
import { useRefDimensions } from "../../utils/useRefDimensions";
import { AccountIsPausedModal } from "../AccountIsPausedModal/AccountIsPausedModal";
import { BackToAdminAccountBanner } from "../BackToAdminAccountBanner/BackToAdminAccountBanner";
import { BackToRequestingUserAccountBanner } from "../BackToRequestingUserAccountBanner/BackToRequestingUserAccountBanner";
import { MembershipUpgradeModal } from "../Membership/MembershipUpgradeModal";
import { SideNav } from "../SideNav/SideNav.component";
import { SideNavHeader } from "../SideNav/SideNavHeader";
import { SubscriptionBanner } from "../SubscriptionBanner/SubscriptionBanner.component";
import styles from "./styles.module.scss";
import { useSidebarsShowState } from "./useSidebarsShowState";

export const BaseAppLayout: React.FC = ({ children }) => {
  const location = useLocation();
  const [isDrawerOpened, setIsDrawerOpened] = React.useState(false);

  const [footerHeight, setFooterHeight] = React.useState(0);

  const { setIsSideNavOpened, isSideNavOpened } = useSidebarsShowState();

  const isMobile = useIsMobile();

  const headerRef = useRef<HTMLElement | null>(null);
  const headerDimensions = useRefDimensions(headerRef);

  useEffect(() => {
    if (isDrawerOpened) {
      document.body.classList.add(styles.bodyOverflowHidden);
    } else {
      document.body.classList.remove(styles.bodyOverflowHidden);
    }
  }, [isDrawerOpened]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (
      searchParams.get("showModal") === "referrals" ||
      searchParams.get("bigHappyFamily") === "hellYeah"
    ) {
      setIsDrawerOpened(true);
    }
  }, [location]);

  const store = useWSStore();
  const bulkReviewVisible = store.bulkReviewVisible && !!store.bulkReview;

  return (
    <>
      <AccountIsPausedModal />
      <MembershipUpgradeModal />

      <WSElement
        className={styles.container}
        key="container"
        data-navigationisopen={isMobile ? isDrawerOpened : isSideNavOpened}
        data-testid="appContainer"
        data-headerheight={headerDimensions.height}
        data-bulkreviewvisible={bulkReviewVisible}
      >
        <style>
          {`
            .${styles.content} > *:first-child:not(.${styles.footer}):not([class*="WSLayout"]) {
              padding-bottom: ${footerHeight}px;
            }
          `}
        </style>
        <WSElement ref={headerRef} className={styles.header} key="header">
          <WSScreen.Mobile>
            <SideNavHeader
              isDrawerOpened={isDrawerOpened}
              setIsDrawerOpened={setIsDrawerOpened}
            />
          </WSScreen.Mobile>

          <BackToAdminAccountBanner />
          <BackToRequestingUserAccountBanner />
          <SubscriptionBanner />

          {bulkReviewVisible && (
            <WSScreen.Mobile>
              <BulkReviewPanel {...store.bulkReview!} />
            </WSScreen.Mobile>
          )}
        </WSElement>

        <SideNav
          isDrawerOpened={isDrawerOpened}
          setIsDrawerOpened={setIsDrawerOpened}
          isSideNavOpened={isSideNavOpened}
          setIsSideNavOpened={setIsSideNavOpened}
          desktopTopPadding={headerDimensions.height}
        />

        {bulkReviewVisible && (
          <WSScreen.TabletAndDesktop>
            <WSElement
              className={styles.bulkReviewWrapper}
              style={{ top: headerDimensions.height }}
            >
              <BulkReviewSidebar {...store.bulkReview!} />
            </WSElement>
          </WSScreen.TabletAndDesktop>
        )}

        <WSElement
          className={styles.main}
          style={{
            paddingTop: headerDimensions.height
          }}
        >
          <WSElement className={styles.content} key="content">
            {children}
            <AppFooter
              className={styles.footer}
              onChangeFooterHeight={height => {
                setFooterHeight(height);
              }}
            />
          </WSElement>
        </WSElement>
      </WSElement>
    </>
  );
};
